import React, { useEffect, useState } from "react";
import Dialog from "components/organisms/Dialog/Dialog";
import { faCircleNotch, faCheck, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { duplicateQuickRequest, duplicateQuoteRequest } from "api";

import Button from "components/atoms/Button/Button";
import errorToast from "helpers/errorToast";
import testIds from "constants/testIds";

interface IDuplicateDialogProps {
  isOpen: boolean;
  close: () => void;
  type: string;
  reference: string;
}

function DuplicateDialog({ isOpen, close, type, reference }: IDuplicateDialogProps) {
  const intl = useIntl();
  const isQuickRequest = type === "quick";
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<any>({});
  const [error, setError] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) duplicate();
  }, [isOpen]);

  const duplicate = () => {
    setIsLoading(true);
    const duplicateFunc = isQuickRequest ? duplicateQuickRequest : duplicateQuoteRequest;
    duplicateFunc(reference)
      .then((res) => {
        setData(res.data);
        setSuccess(true);
      })
      .catch((err) => {
        setError(true);
        errorToast(err);
      })
      .then(() => setIsLoading(false));
  };

  const getType = () => {
    switch (type) {
      case "quick":
        return "Quick Request";
      case "quote":
        return "Quote Request";
      case "shipment":
        return "Shipment";
      default:
        return "";
    }
  };

  const handleFormRedirect = () => {
    if (type === "quick") navigate(`/quick-request?draft_id=${data.reference_number}&is_duplicated=true`);
    if (type === "quote" || type === "shipment")
      navigate(`/quote-request?draft_id=${data.reference_number}&is_duplicated=true`);
  };

  return (
    <Dialog
      size="md"
      show={isOpen}
      onHide={close}
      title={intl.formatMessage(
        {
          id: "shipment-quotes.components.duplicateDialog.duplicateType",
          defaultMessage: `Duplicate {type}`,
        },
        {
          type: getType(),
        },
      )}
    >
      <div className="text-center">
        {isLoading && <FontAwesomeIcon icon={faCircleNotch} style={{ fontSize: 30 }} className="text-secondary" spin />}
        {success && !isLoading && (
          <div>
            <FontAwesomeIcon className="text-success" icon={faCheck} style={{ fontSize: 30 }} />
            <p className="mt-3 text-secondary">
              <FormattedMessage
                id="shipment-quotes.components.duplicateDialog.duplicateSuccess"
                values={{ type: getType() }}
                defaultMessage={`You've successfully duplicated your ${getType()}.`}
              />
            </p>

            <div className="d-flex justify-content-center pt-3">
              <Button
                className="btn-grey"
                label={intl.formatMessage({
                  id: "report.discard",
                  defaultMessage: "Discard",
                })}
                onClick={close}
              />

              <Button
                className="ml-2"
                variant="success"
                onClick={handleFormRedirect}
                data-testid={testIds.LIST_DUPLICATE_CONFIRM_BUTTON}
              >
                <FormattedMessage id="login.continue" defaultMessage="Continue" />
              </Button>
            </div>
          </div>
        )}
        {error && (
          <div>
            <FontAwesomeIcon className="text-danger" icon={faExclamationCircle} style={{ fontSize: 30 }} />
            <p className="text-secondary mt-3">
              <FormattedMessage
                id="quote.listing.duplicateError"
                values={{ type: getType() }}
                defaultMessage={`There was an error duplicating your ${getType()}.`}
              />
            </p>
            <div className="d-flex justify-content-center pt-3">
              <Button
                className="btn-grey"
                label={intl.formatMessage({
                  id: "general.discard",
                  defaultMessage: "Discard",
                })}
                onClick={close}
              />
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

DuplicateDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  reference: PropTypes.string.isRequired,
};

export default DuplicateDialog;
